// constants.js
import { alpha, styled } from '@mui/material/styles';
import { Button, Toolbar } from '@mui/material';
import config from "../config";
export const server = `${config.server}/api`;
export const API_BASE = server
export const WS_BASE = config.ws_server;
export const INACTIVE_TIMEOUT = 15 * 60 * 1000;

// ==========================================
// 工具函数
// ==========================================
export const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  let filename = imageUrl;
  
  if (filename.includes('/')) {
    const parts = filename.split('/');
    filename = parts[parts.length - 1];
  }
  
  if (filename.startsWith('/')) {
    filename = filename.substring(1);
  }
  
  return `${API_BASE}/serve-image/${filename}`;
};

export const compressImage = (file, maxSizeKB = 200) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('無效的圖片文件'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onerror = () => {
      reject(new Error('圖片載入失敗'));
    };
    
    img.onload = () => {
      try {
        let { width, height } = img;
        
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const maxDimension = isMobile ? 600 : 800; 
        
        if (width > height && width > maxDimension) {
          height = Math.floor((height * maxDimension) / width);
          width = maxDimension;
        } else if (height > maxDimension) {
          width = Math.floor((width * maxDimension) / height);
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        let quality = isMobile ? 0.7 : 0.8;
        const minQuality = 0.3; 
        
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('圖片壓縮失敗'));
                return;
              }
              
              if (blob.size <= maxSizeKB * 1024 || quality <= minQuality) {
                const compressedFile = new File([blob], file.name, { 
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                URL.revokeObjectURL(img.src);
                resolve(compressedFile);
              } else {
                quality -= 0.1;
                tryCompress();
              }
            },
            'image/jpeg',
            quality
          );
        };
        tryCompress();
      } catch (error) {
        reject(new Error('圖片處理失敗: ' + error.message));
      }
    };
    
    try {
      img.src = URL.createObjectURL(file);
    } catch (error) {
      reject(new Error('無法讀取圖片文件'));
    }
  });
};

export const validateImageFile = (file, maxSizeMB = 10) => {
  if (!file) return { valid: false, error: '請選擇文件' };
  if (!file.type.startsWith('image/')) return { valid: false, error: '請選擇圖片文件' };
  
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: '不支持的圖片格式，請使用 JPG、PNG、GIF 或 WebP' };
  }
  
  if (file.size > maxSizeMB * 1024 * 1024) {
    return { valid: false, error: `圖片大小不能超過 ${maxSizeMB}MB` };
  }
  return { valid: true };
};

export const checkAuthStatus = () => {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return !!token;
};

export const getAuthToken = () => {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export const getCurrentUser = async () => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/profile`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('獲取用戶資訊失敗:', error);
  }
  return null;
};

export const handleMentionClick = (messageId) => {
  const element = document.getElementById(`message-${messageId}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.style.backgroundColor = 'rgba(255,255,0,0.3)';
    setTimeout(() => {
      element.style.backgroundColor = '';
    }, 2000);
  }
};

// ==========================================
// 圖表相關配置 (含 SEO 整合)
// ==========================================

export const chartTitles = {
  keyAssets: "關鍵投資品價格走勢圖",
  oil: "美債石油關係圖",
  gold: "美債黃金關係圖",
  bitcoin: "美債比特幣關係圖",
  usd: "美債美元關係圖",
  usStocks: "美債美股關係圖",
  stockGold: "美股黃金關係圖",
  stockBitcoin: "美股比特幣關係圖",
  usJobs: "美國非農數據",
  chinaPBOC: "中國社會融資規模存量統計表",
  trade: "美國進出口中國貨物數據",
  forex: "美元兌換主要經濟體貨幣的匯率",
  sentiment: "市場情緒與美股關係圖",
  marketShare: "一籃子貨幣市場份額",
  freedom: '自由組合關係圖',
  usDebt: '美國國債原始數據',
  usGDP: '美國國內生產總值分類統計',
  rareEarth: '中國稀土出口數據',
};

// ✅ 圖表與 SEO 最終整合版配置
export const chartConfigs = {
  keyAssets: {
    endpoint: "關鍵投資品",
    type: "line",
    seo: {
      title: "關鍵投資標的即時行情、最硬核財經資訊 - 經濟簡訊網",
      description: "提供黃金、比特幣、WTI原油及台積電ADR等核心投資商品的即時價格走勢與交叉對比分析。",
      keywords: "關鍵投資商品, 比特幣走勢, 黃金價格, 原油行情, 經濟簡訊網",
      spiderText: "本頁面追蹤全球關鍵投資商品價格走勢。黃金與比特幣的資金流向反映了市場避險與高風險資產的偏好, 而WTI原油則預示通膨預期。透過這些歷史數據, 投資者能精確掌握總體經濟脈動。"
    }
  },
  oil: {
    endpoint: ["美債", "石油"], 
    type: "dualLine",
    leftLabel: "10年期美債收益率 (%)",
    rightLabel: "WTI原油價格 (USD)",
    seo: {
      title: "美國十年期國債與WTI原油價格關係 - 經濟簡訊網",
      description: "提供美國十年期國債與WTI原油價格的歷史走勢圖表及專業分析, 助您洞察能源市場與債券市場的互動關係。",
      keywords: "WTI原油價格, 美國十年期國債, 債券與原油關係, 經濟簡訊網",
      spiderText: "美國十年期國債與WTI原油價格的歷史走勢圖表及專業分析, 助您洞察能源市場與債券市場的互動關係。"
    }
  },
  gold: {
    endpoint: ["美債", "黃金"], 
    type: "dualLine",
    leftLabel: "10年期美債收益率 (%)",
    rightLabel: "黃金價格 (USD/oz)",
    seo: {
      title: "美國十年期國債與黃金價格關係 - 經濟簡訊網",
      description: "提供美國十年期國債與黃金價格的歷史走勢圖表及專業分析，助您洞察傳統債券市場與貴金屬的互動關係。",
      keywords: "黃金價格, 美國十年期國債, 債券與黃金關係, 經濟簡訊網",
      spiderText: "美國十年期國債與黃金價格的歷史走勢圖表及專業分析，助您洞察傳統債券市場與貴金屬的互動關係。"
    }
  },
  bitcoin: {
    endpoint: ["美債", "比特幣"], 
    type: "dualLine",
    leftLabel: "10年期美債收益率 (%)",
    rightLabel: "比特幣價格 (USD)",
    seo: {
      title: "美國十年期國債與比特幣價格關係 - 經濟簡訊網",
      description: "提供美國十年期國債與比特幣價格的歷史走勢圖表及專業分析，助您洞察傳統債券市場與加密貨幣的互動關係。",
      keywords: "比特幣價格, 美國十年期國債, 債券與加密貨幣關係, 經濟簡訊網",
      spiderText: "美國十年期國債與比特幣價格的歷史走勢圖表及專業分析，助您洞察傳統債券市場與加密貨幣的互動關係。"
    }
  },
  usd: {
    endpoint: ["美債", "美元"], 
    type: "dualLine",
    leftLabel: "10年期美債收益率 (%)",
    rightLabel: "DXY美元指數",
    seo: {
      title: "美國十年期國債與美元指數(DXY)關係 - 經濟簡訊網",
      description: "提供美國十年期國債與美元指數的歷史走勢圖表及專業分析，助您洞察債券市場與外匯市場的互動關係。",
      keywords: "美元指數, DXY, 美國十年期國債, 匯率與債券, 經濟簡訊網",
      spiderText: "美國十年期國債收益率與美元指數(DXY)通常呈現高度正相關, 本圖表助您分析美國利率政策對強勢美元的推動作用。"
    }
  },
  usStocks: {
    endpoint: ["美債", "美股"], 
    type: "dualLine",
    leftLabel: "10年期美債收益率 (%)",
    rightLabel: "道瓊指數",
    seo: {
      title: "美國十年期國債與美股道瓊指數關係 - 經濟簡訊網",
      description: "提供美國十年期國債與美股道瓊指數的歷史走勢圖表及專業分析，助您洞察債券市場與股票市場的互動關係。",
      keywords: "美國十年期國債, 道瓊斯, 債券與股票關係, 經濟簡訊網",
      spiderText: "美國十年期國債與美股道瓊指數的歷史走勢圖表及專業分析，助您洞察債券市場與股票市場的互動關係。"
    }
  },
  stockGold: {
    endpoint: ["美股", "黃金"], 
    type: "dualLine",
    leftLabel: "道瓊指數",
    rightLabel: "黃金價格 (USD/oz)",
    seo: {
      title: "美股與黃金價格關係 - 經濟簡訊網",
      description: "提供美國主要股票指數(道瓊斯、標普500、納斯達克)與黃金價格的歷史走勢圖表及專業分析, 助您洞察股票市場與貴金屬的互動關係。",
      keywords: "美股價格, 黃金價格, 股票與黃金關係, 經濟簡訊網",
      spiderText: "美國主要股票指數(道瓊斯、標普500、納斯達克)與黃金價格的歷史走勢圖表及專業分析, 助您洞察股票市場與貴金屬的互動關係。"
    }
  },
  stockBitcoin: {
    endpoint: ["美股", "比特幣"], 
    type: "dualLine",
    leftLabel: "道瓊指數",
    rightLabel: "比特幣價格 (USD)",
    seo: {
      title: "美股道瓊斯指數與比特幣價格關係 - 經濟簡訊網",
      description: "提供美國道瓊斯工業平均指數與比特幣價格的歷史走勢圖表及專業分析，助您洞察傳統股票市場與加密貨幣的互動關係。",
      keywords: "比特幣價格, 道瓊斯, 比特幣與道瓊斯關係, 經濟簡訊網",
      spiderText: "美國道瓊斯工業平均指數與比特幣價格的歷史走勢圖表及專業分析，助您洞察傳統股票市場與加密貨幣的互動關係。"
    }
  },
  usJobs: {
    endpoint: ["就業", "失業"], 
    type: "dualBar",
    seo: {
      title: "美國非農就業數據(NFP)與小非農(ADP)歷史數據庫 - 經濟簡訊網",
      description: "收錄1939年至今最完整的美國大非農就業人數與失業率數據, 動態對比小非農趨勢, 助您精確研判聯準會政策。",
      keywords: "美國非農數據, 大非農, ADP, 失業率, 總體經濟指標",
      spiderText: "美國非農就業數據是全球最重要的經濟指標之一，經濟簡訊網提供最完整的美國大非農就業人數與失業率數據庫，動態對比小非農趨勢，助您精確研判聯準會政策。"
    }
  },
  sentiment: {
    endpoint: ["市場情緒", "美股"], 
    type: "dualLine",
    leftLabel: "VIX恐慌指數",
    rightLabel: "道瓊指數",
    seo: {
      title: "市場情緒恐慌指數VIX走勢圖表 - 經濟簡訊網",
      description: "提供市場情緒恐慌指數VIX的歷史走勢圖表, 助您洞察投資者情緒變化與市場趨勢。",
      keywords: "市場情緒恐慌指數, VIX, 投資者情緒, 市場趨勢, 經濟簡訊網",
      spiderText: "VIX指數是衡量市場恐慌情緒的重要指標, 經濟簡訊網提供最完整的VIX歷史數據圖表, 助您洞察投資者情緒變化與市場趨勢。"
    }
  },
  trade: {
    endpoint: ["進口", "出口"], 
    type: "simpleBar",
    seo: {
      title: "中美進出口貿易貨物數據歷史走勢 - 經濟簡訊網",
      description: "精確呈現中美貿易往來、進口總額與出口總額的月度及年度聚合對比圖表。",
      keywords: "中美貿易數據, 進出口總額, 貿易順差, 經濟數據",
      spiderText: "中美貿易數據是全球經濟的重要風向標，經濟簡訊網提供最完整的中美貿易往來、進口總額與出口總額的月度及年度聚合對比圖表，助您洞察全球經濟動態。"
    }
  },
  marketShare: {
    endpoint: "市場份額", 
    type: "pie",
    seo: {
      title: "一籃子貨幣市場份額 - 經濟簡訊網",
      description: "提供美元、人民幣、歐元、日圓、英鎊等主要貨幣在全球外匯市場的歷史市場份額圖表，助您洞察貨幣競爭格局與趨勢。",
      keywords: "貨幣市場份額, 美元, 人民幣, 歐元, 日圓, 英鎊, 外匯市場, 經濟簡訊網",
      spiderText: "美元、人民幣、歐元、日圓、英鎊等主要貨幣在全球外匯市場的歷史市場份額圖表，助您洞察貨幣競爭格局與趨勢。"
    }
  },
  forex: {
    endpoint: "美元匯率",
    type: "table",
    seo: {
      title: "主要貨幣對美元的匯率走勢圖表 - 經濟簡訊網",
      description: "提供美元兌人民幣、歐元、日圓、英鎊等主要貨幣的歷史匯率走勢圖表，助您洞察外匯市場動態。",
      keywords: "美元匯率, 人民幣, 歐元, 日圓, 英鎊, 外匯市場",
      spiderText: "美元兌人民幣、歐元、日圓、英鎊等主要貨幣的歷史匯率走勢圖表，助您洞察外匯市場動態。"
    }
  },
  chinaPBOC: {
    endpoint: "社會融資規模存量",
    type: "StackedAreaLine",
    seo: {
      title: "中國社會融資規模存量統計表 - 經濟簡訊網",
      description: "提供中國人民銀行社會融資規模存量的歷史數據與走勢圖表，助您洞察中國貨幣政策與經濟動態。",
      keywords: "中國社會融資規模, 人民銀行, 貨幣政策, 經濟數據, 經濟簡訊網",
      spiderText: "中國社會融資規模是衡量中國貨幣政策與經濟動態的重要指標，經濟簡訊網提供最完整的中國社會融資規模數據圖表，助您洞察中國經濟全貌。"
    }
  },
  usDebt: {
    endpoint: "美國國債數據",
    type: "StackedAreaDualLine",
    seo: {
      title: "美國國債數據與走勢圖表 - 經濟簡訊網",
      description: "提供美國國債的歷史數據與走勢圖表，助您洞察美國財政狀況與經濟動態。",
      keywords: "美國國債, 國債數據, 國債走勢, 經濟數據, 經濟簡訊網",
      spiderText: "美國國債是全球最大的債券市場，經濟簡訊網提供最完整的美國國債數據圖表，助您洞察美國財政全貌。"
    }
  },
  usGDP: {
    endpoint: "美國GDP數據",
    type: "StackedAreaDualBar",
    seo: {
      title: "美國國內生產總值分類統計 - 經濟簡訊網",
      description: "提供美國國內生產總值(GDP)的歷史數據與走勢圖表, 助您洞察美國經濟增長與結構變化。",
      keywords: "美國GDP, 國內生產總值, GDP數據, 經濟增長, 經濟結構, 經濟簡訊網",
      spiderText: "美國GDP是全球最重要的經濟指標之一, 經濟簡訊網提供最完整的美國GDP數據圖表, 助您洞察美國經濟全貌。"
    }
  },
  rareEarth: {
    endpoint: "中國稀土出口數據",
    type: "bar",
    seo: {
      title: "中國稀土出口數據 - 經濟簡訊網",
      description: "提供中國稀土出口數據的歷史走勢圖表，助您洞察稀土市場動態與投資機會。",
      keywords: "稀土出口, 稀土數據, 稀土市場, 經濟簡訊網",
      spiderText: "稀土是中國產業王牌也是中國的命門，中共一旦玩脫，後果不堪設想。"
    }
  },
  freedom: {
    endpoint: ["自由組合1", "自由組合2"],
    type: "dualLinePlus",
    seo: {
      title: "自由組合圖表 - 經濟簡訊網",
      description: "提供多種經濟指標的自由組合圖表，助您根據個人需求靈活分析不同數據之間的關聯與趨勢。",
      keywords: "自由組合圖表, 經濟指標, 數據分析, 經濟簡訊網",
      spiderText: "自由組合圖表讓您可以根據個人需求靈活分析不同經濟指標之間的關聯與趨勢，助您做出更明智的投資決策。"
    }
  },
};

// 預設的 SEO Fallback (避免未設定 SEO 的路由崩潰)
export const getDefaultSeo = () => ({
  title: "全球總體經濟圖表與財經指標分析 - 經濟簡訊網",
  description: "提供即時全球財經新聞、總經數據、匯率及關鍵投資商品歷史價格走勢圖表。",
  keywords: "經濟簡訊網, 財經數據, 總體經濟指標, 歷史圖表",
  spiderText: "經濟簡訊網致力於提供最直觀、精確的總體經濟數據圖表，免除繁雜的廣告干擾，讓真實的數據說話。"
});

// ==========================================
// 其他常數與圖標
// ==========================================

export const indicatorMap = {
  0: "英偉達",
  1: "台積電", 
  2: "阿斯麥",
  3: "WTI原油",
  4: "黃金",
  5: "比特幣",
  6: "10年期美債",
  7: "DXY美元指數",
  8: "VIX恐慌指數",
  9: "道瓊指數",
};

export const tradeMap = {
  "ALL":"總額",
  "85":"電子電氣類",
  "84":"核反應類",
  "95":"玩具運動類",
  "39":"塑膠類",
  "87":"汽車配件類",
  "94":"家具類",
  "90":"光學儀器類",
  "73":"鋼鐵類",
  "62":"非針織服裝類",
  "61":"針織服裝類",
  "63":"其他紡織類",
  "64":"鞋類",
  "30":"藥品類",
  "88":"航空器類",
  "27":"礦物燃料類",
  "29":"有機化學類",
  "38":"雜項化學類",
  "10":"穀物類",
  "74":"銅類",
  "02":"肉類",
  "00":"其他類",
};

export const colorMap = {
  "電子電氣類": "#0095ffff",
  "核反應類": "#923600ff",
  "玩具運動類": "#ffbb00ff",
  "塑膠類": "#1d326dff",
  "汽車配件類": "#6c6dbbff",
  "家具類": "#635d0dff",
  "光學儀器類": "#ff0000ff",
  "鋼鐵類": "#9995b3ff",
  "非針織服裝類": "#8800a3ff",
  "針織服裝類": "#9e51b1ff",
  "其他紡織類": "#f200ffff",
  "鞋類": "#5f005aff",
  "藥品類": "#8ec98eff",
  "航空器類": "#00f7ffff",
  "礦物燃料類": "#423d0eff",
  "有機化學類": "#00ff00ff",
  "雜項化學類": "#026b02ff",
  "穀物類": "#cac700ff",
  "銅類": "#ff7300ff",
  "肉類": "#cf6072ff",
  "其他類": "#000000ff",
};

export const ranges = [
  { key: 30, label: "30天" },
  { key: 90, label: "90天" },
  { key: 180, label: "180天" },
  { key: 365, label: "1年" },
  { key: 365 * 3, label: "3年" },
  { key: 365 * 5, label: "5年" },
  { key: 365 * 100, label: "全部" },
];

export const rangesM = [
  { key: 365+31, label: "1年" },
  { key: 365 * 3+31, label: "3年" },
  { key: 365 * 5+31, label: "5年" },
  { key: 365 * 10+31, label: "10年" },
  { key: 365 * 100+31, label: "全部" },
];

export const rangesS = [
  { key: 365+31, label: "1年" },
  { key: 365 * 3+31, label: "3年" },
  { key: 365 * 5+31, label: "5年" },
  { key: 365 * 10+31, label: "10年" },
  { key: 365 * 20+31, label: "20年" },
  { key: 365 * 100+31, label: "全部" },
];

// Icons
export const GoldBarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="gold">
    <rect x="4" y="10" width="16" height="8" rx="2" ry="2" />
    <polygon points="4,10 12,4 20,10" fill="goldenrod" />
  </svg>
);

export const GoldStockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24">
    <polyline points="4,16 8,12 12,14 16,8 20,10" fill="none" stroke="green" strokeWidth="2" />
    <rect x="6" y="17" width="12" height="5" rx="1" ry="1" fill="gold" />
    <polygon points="6,17 12,13 18,17" fill="goldenrod" />
  </svg>
);

export const BitcoinIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#f7931a" />
    <path fill="#fff" d="M20.9 14.7c.3-2-1.2-3-3.3-3.7l.7-2.8-1.7-.4-.7 2.8c-.5-.1-1.1-.2-1.6-.3l.7-2.9-1.7-.4-.7 2.8c-.4-.1-.7-.2-1-.3h-.1l-2.3-.6-.5 1.9s1.2.3 1.2.3c.7.2.8.6.8 1l-2 7.8c-.1.2-.2.5-.6.4 0 0-1.2-.3-1.2-.3l-.9 1.9 2.2.6c.4.1.8.2 1.2.3l-.7 2.9 1.7.4.7-2.8c.6.2 1.1.3 1.7.4l-.7 2.8 1.7.4.7-2.9c2.9.6 5.1.3 6-2.3.7-2.1 0-3.3-1.6-4.1 1.1-.3 1.9-1 2.1-2.6zm-3.7 5.6c-.5 2.1-4 .9-5.1.6l.9-3.5c1.1.3 4.7.9 4.2 2.9zm.5-5.6c-.4 1.7-3 1-3.8.7l.7-2.9c.8.2 3.5.7 3.1 2.2z" />
  </svg>   
);

export const BitcoinStockIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#f7931a" />
    <path fill="#fff" d="M20.9 14.7c.3-2-1.2-3-3.3-3.7l.7-2.8-1.7-.4-.7 2.8c-.5-.1-1.1-.2-1.6-.3l.7-2.9-1.7-.4-.7 2.8c-.4-.1-.7-.2-1-.3h-.1l-2.3-.6-.5 1.9s1.2.3 1.2.3c.7.2.8.6.8 1l-2 7.8c-.1.2-.2.5-.6.4 0 0-1.2-.3-1.2-.3l-.9 1.9 2.2.6c.4.1.8.2 1.2.3l-.7 2.9 1.7.4.7-2.8c.6.2 1.1.3 1.7.4l-.7 2.8 1.7.4.7-2.9c2.9.6 5.1.3 6-2.3.7-2.1 0-3.3-1.6-4.1 1.1-.3 1.9-1 2.1-2.6zm-3.7 5.6c-.5 2.1-4 .9-5.1.6l.9-3.5c1.1.3 4.7.9 4.2 2.9zm.5-5.6c-.4 1.7-3 1-3.8.7l.7-2.9c.8.2 3.5.7 3.1 2.2z" />
    <polyline points="6,22 12,14 18,16 24,10 28,12" fill="none" stroke="green" strokeWidth="2" />
  </svg>
);

export const freedomIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="15" height="15" role="img" aria-label="Square flag: left blue, right yellow">
    <title>Blue-Yellow Split Square</title>
    <rect x="0" y="0" width="50" height="100" fill="#0057B7"/>
    <rect x="50" y="0" width="50" height="100" fill="#FFD700"/>
  </svg>
);

// 資料轉換函數
export function convertValue(name, value){
  if(name === '黃金'){
    return value / 31.1035; 
  } else if(name === '比特幣'){
    return value / 1000; 
  }
  return value; 
};

export const toYYYYMM = (v) => {
  if (typeof v === 'string') return v.slice(0, 7);           
  const d = new Date(v);                                      
  return isNaN(d) ? String(v) : d.toISOString().slice(0, 7);  
};

export function fillMissingValues(values) {
  const filled = [...values];
  for (let i = 0; i < filled.length; i++) {
    if (filled[i] == null) {
      let prev = null, next = null;
      for (let j = i - 1; j >= 0; j--) {
        if (filled[j] != null) { prev = filled[j]; break; }
      }
      for (let j = i + 1; j < filled.length; j++) {
        if (filled[j] != null) { next = filled[j]; break; }
      }

      if (prev != null && next != null) {
        filled[i] = (prev + next) / 2;  
      } else if (prev != null) {
        filled[i] = prev; 
      } else if (next != null) {
        filled[i] = next; 
      }
    }
  }
  return filled;
};

export function toMap(data){
  return new Map(data.map((d) => [d.date, d.value]));
};

export function aggregateYearly(data, method = "average") {
  const yearly = {};

  data.forEach(({ date, value }) => {
    const year = date.slice(0, 4); 
    if (!yearly[year]) yearly[year] = [];
    yearly[year].push(value);
  });

  return Object.entries(yearly).map(([year, values]) => {
    let agg;
    if (method === "sum") {
      agg = values.reduce((a, b) => a + b, 0);
    } else {
      agg = values.reduce((a, b) => a + b, 0) / values.length;
    }
    return { year, value: agg };
  });
};

function utc2local(utc_str) {
    if (!utc_str) return '';
    const utc = new Date(utc_str);
    let offset = utc.getTimezoneOffset();
    let local = new Date(utc.getTime() - offset * 60000);

    const year = local.getFullYear()
    const month = (local.getMonth() + 1).toString().padStart(2, '0');
    const day = local.getDate().toString().padStart(2, '0');
    const hours = local.getHours().toString().padStart(2, '0');
    const minutes = local.getMinutes().toString().padStart(2, '0');
    
    return `${year}年${month}月${day}日 ${hours}:${minutes}`;
}

export const utc2localday = (utc_str) => {
    if (!utc_str) return '';
    const utc = new Date(utc_str);
    let offset = utc.getTimezoneOffset();
    let local = new Date(utc.getTime() - offset * 60000);

    const year = local.getFullYear()
    const month = (local.getMonth() + 1).toString().padStart(2, '0');
    const day = local.getDate().toString().padStart(2, '0');
    
    return `${year}年${month}月${day}日`;
}

export const formatDate = (dateString) => {
    if (!dateString) return '從未登入';
    return utc2local(dateString);
};

export const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const utc = new Date(dateString);
    let offset = utc.getTimezoneOffset();
    const date = new Date(utc.getTime() - offset * 60000);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '剛剛';
    if (diffInHours < 24) return `${diffInHours}小時前`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays}天前`;
    
    return formatDate(dateString);
};

export const getVipDaysLeft = (expiresAt) => {
    if (!expiresAt) return 0;
    const expireDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expireDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
};

export const getRiskStatusInfo = (status) => {
    switch (status) {
      case 'NORMAL':
        return { color: 'success', text: '正常' };
      case 'AD':
        return { color: 'warning', text: '廣告戶' };
      case 'WASH':
        return { color: 'error', text: '水軍' };
      case 'BOT':
        return { color: 'error', text: '機器人' };
      default:
        return { color: 'default', text: '未知' };
    }
};

export function formatUTCToLocalTime(input, locale = undefined, options = { hour: '2-digit', minute: '2-digit' }) {
  if (input == null) return '';

  let date;
  if (typeof input === 'number') {
    const ms = input < 1e12 ? input * 1000 : input;
    date = new Date(ms);
  } else if (typeof input === 'string') {
    let s = input.trim();
    const tzRegex = /([zZ]|[+\-]\d{2}:\d{2}$|[+\-]\d{4}$|[+\-]\d{2}$)/;
    if (tzRegex.test(s)) {
      date = new Date(s);
    } else {
      s = s.replace(' ', 'T');
      date = new Date(s + 'Z');
    }
  } else {
    date = new Date(input);
  }

  if (isNaN(date)) return '';
  return date.toLocaleTimeString(locale, options);
}

// Styled Components
export const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

export const SilverButton = styled(Button)(() => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  textTransform: 'none',
  background: 'linear-gradient(90deg, #444444, #888888, #444444)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

export const ActiveSilverButton = styled(SilverButton)({
  borderBottom: '2px solid #555',
  borderRadius: 0,
});